import { Badge, Card } from "@procurement-agents/ui";
import type { RunState } from "@procurement-agents/scope-api";

const BASIS: Record<string, string> = {
  intake_value: "from the value band, using the value given in intake",
  inferred_value: "from the value band, using a value the agent inferred",
  fallback: "the tenant default (no value band matched)",
  procurement_override: "chosen by the procurement user, overriding the value band",
  recommended: "as market research recommended",
  default: "the customer's default; market research recommended nothing else",
  override: "chosen by the procurement user",
};

const money = (n: number | null | undefined, cur: string) => (n == null ? null : `${n.toLocaleString()} ${cur}`);

/** What was decided about the shape of this procurement, and why: the three axes, what the market view said about
 *  each, the rules that applied, the drafting style that followed, and any research the user sent back. */
export function StructureReader({ state }: { state: RunState }) {
  const intel = state.market_intel;
  const req = state.requisition;
  const tenant = state.tenant_view;
  const structure = tenant?.structures?.find((s) => s.id === state.selected_structure_id);
  const model = tenant?.contract_models?.find((m) => m.id === state.selected_contract_model_id);
  const approach = tenant?.specification_approaches?.find((a) => a.id === state.selected_specification_approach_id);
  const decided = (state.audit_log ?? []).find((e) => e.node === "apply_structure_decision");
  const reruns = state.research_reruns ?? 0;

  return (
    <Card title="Structure decision">
      <Axis
        label="Procurement method"
        chosen={structure?.name ?? state.selected_structure_id ?? "not decided"}
        basis={BASIS[state.structure_basis ?? ""] ?? state.structure_basis}
        detail={req?.estimated_value != null ? `Estimated value ${money(req.estimated_value, req.currency)} (${req.estimated_value_source})` : null}
        agrees={intel ? intel.recommended_structure_id == null ? null : intel.recommended_structure_id === state.selected_structure_id : undefined}
        recommended={tenant?.structures?.find((s) => s.id === intel?.recommended_structure_id)?.name ?? intel?.recommended_structure_id ?? null}
        rationale={intel?.recommendation_rationale ?? null}
        rules={structure?.rules ?? []}
      />
      {(model || state.selected_contract_model_id) && (
        <Axis
          label="Contract model"
          chosen={model?.name ?? state.selected_contract_model_id!}
          basis={BASIS[state.contract_model_basis ?? ""] ?? state.contract_model_basis}
          detail={state.selected_lots?.length ? `Lots: ${state.selected_lots.join(", ")}` : null}
          agrees={intel ? intel.recommended_contract_model_id == null ? null : intel.recommended_contract_model_id === state.selected_contract_model_id : undefined}
          recommended={tenant?.contract_models?.find((m) => m.id === intel?.recommended_contract_model_id)?.name ?? intel?.recommended_contract_model_id ?? null}
          rationale={intel?.contract_model_rationale ?? null}
          note={intel?.supplier_capacity_note ?? null}
          rules={model?.rules ?? []}
        />
      )}
      {(approach || state.selected_specification_approach_id) && (
        <Axis
          label="Specification approach"
          chosen={approach?.name ?? state.selected_specification_approach_id!}
          basis={BASIS[state.specification_approach_basis ?? ""] ?? state.specification_approach_basis}
          detail={approach?.description ?? null}
          agrees={intel ? intel.recommended_specification_approach_id == null ? null : intel.recommended_specification_approach_id === state.selected_specification_approach_id : undefined}
          recommended={tenant?.specification_approaches?.find((a) => a.id === intel?.recommended_specification_approach_id)?.name ?? intel?.recommended_specification_approach_id ?? null}
          rationale={intel?.specification_approach_rationale ?? null}
          note={intel?.market_capability_note ?? null}
          rules={approach?.rules ?? []}
        />
      )}
      {state.verbosity && (
        <section className="decision-row">
          <h4>Level of detail</h4>
          <p className="decision-selected"><strong>{state.verbosity}</strong> <span className="muted">· {state.verbosity_basis === "override" ? "chosen by the procurement user" : state.verbosity_basis === "configured" ? "set in the agent configuration" : state.verbosity_basis === "default" ? "the customer's default" : "from the customer's rules"}</span></p>
        </section>
      )}
      {reruns > 0 && (
        <section className="decision-row">
          <h4>Market research</h4>
          <p className="muted small">Run {reruns + 1} times: the procurement user sent it back with instructions.</p>
          <ol className="instructions-list">{(state.research_instructions ?? []).map((i, k) => <li key={k}>{i}</li>)}</ol>
        </section>
      )}
      {decided && <p className="muted small decided-line">{decided.summary}{decided.ts ? ` · ${new Date(decided.ts).toLocaleString()}` : ""}</p>}
    </Card>
  );
}

function Axis({ label, chosen, basis, detail, agrees, recommended, rationale, note, rules }: {
  label: string; chosen: string; basis?: string | null; detail?: string | null; agrees?: boolean | null;
  recommended?: string | null; rationale?: string | null; note?: string | null; rules: string[];
}) {
  return (
    <section className="decision-row" aria-label={label}>
      <h4>{label}</h4>
      <p className="decision-selected"><strong>{chosen}</strong>{basis && <span className="muted"> · {basis}</span>}</p>
      {detail && <p className="muted small">{detail}</p>}
      {agrees !== undefined && (
        <p className="market-view">
          {agrees === null ? <Badge>market research made no recommendation</Badge>
            : agrees ? <Badge tone="good">✓ market research agrees</Badge>
            : <Badge tone="warn">market research suggested {recommended}</Badge>}
          {rationale && <span className="muted"> {rationale}</span>}
        </p>
      )}
      {note && <p className="muted small">{note}</p>}
      {rules.length > 0 && (
        <div className="fixed-text" aria-label={`Rules for ${chosen}`}>
          <span className="small muted">Rules applied (policy, quoted)</span>
          <ul>{rules.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
    </section>
  );
}
